import * as THREE from 'three';
import { CROUCH_EYE, CROUCH_LERP_SPEED, STAND_EYE } from '../core/constants.ts';
import { emptyInput, type PlayerInput } from '../core/sim.ts';
import type { PlayerState } from '../core/player.ts';

const MOUSE_SENSITIVITY = 0.0022;

/**
 * Horizontal field of view in degrees, the number games actually quote. Three.js
 * wants vertical, so it is converted per aspect ratio — otherwise an ultrawide
 * monitor silently gets a fisheye lens. Quake and Counter-Strike sit near 90–106.
 */
const HORIZONTAL_FOV = 100;

/** Vertical fov is capped, so a tall narrow window loses horizontal framing
 * rather than gaining a vertical fisheye. Standard "Hor+ with a vertical cap". */
export const MAX_VERTICAL_FOV = 90;

export function verticalFovFor(horizontalDeg: number, aspect: number): number {
  const h = (horizontalDeg * Math.PI) / 180;
  const v = (2 * Math.atan(Math.tan(h / 2) / aspect) * 180) / Math.PI;
  return Math.min(v, MAX_VERTICAL_FOV);
}

/**
 * First-person controller. Rapier's character controller does the collide-and-slide
 * so we get stairs and door frames for free; the resulting position is written
 * back into the simulation's player state each frame.
 *
 * Movement integration lives here rather than in core because Rapier owns
 * collision. When the server becomes authoritative this file's output becomes a
 * prediction that the server confirms or corrects.
 */
export class FirstPersonController {
  readonly camera: THREE.PerspectiveCamera;

  private yaw = 0;
  private pitch = 0;
  /** Tab presses since the session began. Only ever increases. */
  private elementCycles = 0;

  /**
   * Hands the controller a way to ask whether the keyboard is currently the
   * chat line's rather than the game's.
   */
  suspendWhile(typing: () => boolean): void {
    this.suspended = typing;
  }
  private eyeHeight = STAND_EYE;
  private keys = new Set<string>();
  private mouseDown = false;
  private slot = 0;
  private locked = false;
  /** Asked before every keystroke: is somebody typing rather than playing? */
  private suspended: (() => boolean) | null = null;
  horizontalFov = HORIZONTAL_FOV;

  /** Call on resize. Keeps horizontal framing constant across aspect ratios. */
  applyAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.fov = verticalFovFor(this.horizontalFov, aspect);
    this.camera.updateProjectionMatrix();
  }

  /**
   * The camera and the keyboard, and nothing physical.
   *
   * This class used to own the local player's capsule and character controller,
   * built by its own code with its own numbers — which is how the client came to
   * disagree with the server about step height, snap distance and crouching,
   * and how it came to have nobody else to bump into. The capsule is now the
   * world's, from `net/characterBodies.ts`, like everybody else's.
   */
  constructor() {
    this.camera = new THREE.PerspectiveCamera(74, 16 / 9, 0.1, 500);
    this.horizontalFov = HORIZONTAL_FOV;
  }

  attach(canvas: HTMLCanvasElement): void {
    // Listens on the document, not the canvas: the start prompt is a
    // full-screen overlay with pointer events enabled, so it swallows every
    // click aimed at the canvas underneath it.
    document.addEventListener('click', () => {
      if (this.locked) return;
      // Browsers impose a short cooldown after Escape; a rejection here just
      // means "clicked too soon", and clicking again works.
      Promise.resolve(canvas.requestPointerLock()).catch(() => {});
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === canvas;
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      this.yaw -= e.movementX * MOUSE_SENSITIVITY;
      this.pitch -= e.movementY * MOUSE_SENSITIVITY;
      const limit = Math.PI / 2 - 0.02;
      this.pitch = Math.max(-limit, Math.min(limit, this.pitch));
    });
    document.addEventListener('mousedown', () => { if (this.locked) this.mouseDown = true; });
    document.addEventListener('mouseup', () => { this.mouseDown = false; });
    const OWNED = new Set([
      'Space', 'ControlLeft', 'ControlRight', 'KeyC',
      'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyR', 'KeyG', 'KeyF', 'Tab',
    ]);
    window.addEventListener('keydown', (e) => {
      /*
       * Nothing reaches the game while somebody is typing.
       *
       * Without this, writing "well played" walks you into a wall and fires two
       * spells — the oldest bug in multiplayer games and the one players find in
       * the first thirty seconds. The chat line stops its own keys from
       * propagating, but a key pressed *before* the line takes focus, and the
       * key that opens it, both arrive here.
       */
      if (this.suspended?.()) return;

      // Tab is a press, not a state: counted rather than held, and ignored on
      // auto-repeat so leaning on the key does not spin through the elements.
      if (e.code === 'Tab' && !e.repeat && this.locked) this.elementCycles += 1;
      this.keys.add(e.code);
      const n = Number(e.key);
      if (n >= 1 && n <= 6) this.slot = n - 1;
      // Only while captured, so the page stays usable when it is not.
      if (this.locked && OWNED.has(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => { this.keys.clear(); this.mouseDown = false; });
  }

  /** Builds this frame's input for the simulation. */
  readInput(): PlayerInput {
    const input = emptyInput();
    input.moveForward = (this.keys.has('KeyW') ? 1 : 0) - (this.keys.has('KeyS') ? 1 : 0);
    input.moveRight = (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0);
    input.run = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    input.jump = this.keys.has('Space');
    input.flyUp = this.keys.has('Space');
    // C as well as Ctrl: macOS claims Ctrl for system gestures, and Ctrl is easy
    // to miss when it is the only binding for a thing you rarely do.
    input.flyDown =
      this.keys.has('ControlLeft') || this.keys.has('ControlRight') || this.keys.has('KeyC');
    // One key, two meanings, chosen by where you are. Ctrl and C descend while
    // flying and crouch while on the ground — which works only because a
    // Mentalist folding their legs up mid-air is not a thing we want anyway.
    input.crouch = input.flyDown;
    input.fire = this.mouseDown;
    // Two acts, two keys. G pulls them to you; R stands them up.
    input.callGhost = this.keys.has('KeyG');
    input.resurrect = this.keys.has('KeyR');
    input.selfResurrect = this.keys.has('KeyF');
    input.yaw = this.yaw;
    input.pitch = this.pitch;
    // Held rather than edge-triggered: resending the same slot every tick is
    // idempotent, and nothing can fall through the gap between frames and ticks.
    input.selectSlot = this.slot;
    // Monotonic, and never cleared here: the simulation tracks what it has
    // already applied, so a press between two ticks cannot be dropped.
    input.elementCycles = this.elementCycles;
    return input;
  }



  /**
   * Puts the camera where the simulation says the mage is.
   *
   * Reading rather than deciding, which is the point: the camera follows the
   * authoritative position, so a correction from a server moves the view by
   * construction instead of needing a second path to apply it.
   */
  follow(player: PlayerState, alpha = 1, dt = 0): void {
    this.applyStance(player, dt);
    /*
     * Drawn between the last two simulated positions.
     *
     * The simulation runs at a fixed sixty hertz and a monitor may ask for four
     * times that, so without this the camera holds still and jumps sixty times a
     * second — smooth to look around, because the angle comes straight from the
     * mouse, and stepped to move. It costs one tick of visual latency and buys
     * every frame in between.
     *
     * The *body* is placed at the authoritative position rather than the drawn
     * one, or the capsule would resolve its next move from somewhere it never
     * actually was.
     */
    const was = player.prevPos ?? player.pos;
    const t = Math.min(1, Math.max(0, alpha));
    const at = {
      x: was.x + (player.pos.x - was.x) * t,
      y: was.y + (player.pos.y - was.y) * t,
      z: was.z + (player.pos.z - was.z) * t,
    };

    /*
     * The camera only. The body places itself.
     *
     * This used to also push the capsule to the authoritative position with
     * `setNextKinematicTranslation`, which was the counterweight to `moveBody`
     * resolving from wherever Rapier had last stepped the body to. `moveBody`
     * now starts from `player.pos` every time, so the capsule is at the
     * authoritative position by construction and a second placement here could
     * only ever disagree with it — a pending "next" translation applied at some
     * later step, from a frame whose position had already been superseded.
     */
    this.camera.position.set(at.x, at.y + this.eyeHeight, at.z);
    this.camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
  }



  /**
   * Resizes the capsule to the stance core has settled on, and slides the camera
   * to match.
   *
   * The collider snaps and the eye glides. Snapping the collider keeps the
   * hitbox honest — core is already treating the mage as short, and a body that
   * lagged behind would be shootable where it no longer is — while an eye that
   * teleported half a metre downward reads as a glitch rather than a crouch.
   */
  /**
   * The eye follows the simulation's stance, eased so a crouch reads as ducking
   * rather than as being teleported half a metre down. The capsule itself is the
   * world's (`CharacterBodies`) and resizes the instant core says so.
   */
  private applyStance(player: PlayerState, dt: number): void {
    const wanted = player.crouching ? CROUCH_EYE : STAND_EYE;
    const step = CROUCH_LERP_SPEED * dt;
    this.eyeHeight += Math.max(-step, Math.min(step, wanted - this.eyeHeight));
  }


  teleport(player: PlayerState): void {
    // The capsule is placed by the world (`GameWorld.place`); the controller
    // only forgets the momentum the old position had.
    player.verticalVelocity = 0;
    player.vel.x = 0;
    player.vel.z = 0;
  }

}
